/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */

import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { handleCommaSeparatedAmount } from '../../../../../../SharedModules/CommonUtilities/commonUtilities';
import { dispatchTableLoader } from '../../../../../../SharedModules/Table/store/Actions';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const getHeader = (currentPage, pageCount, data) => {
  const { qtr, prinDate, requestedBy, reportName, requestorUserId } = data;
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [28.8, 20, 28.8, 25.92],
    style: 'tableExample',
    table: {
      widths: [230, 230, 240],
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Date Printed: ', bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: [{ text: ' ', bold: true }, ''],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: qtr,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
  return header;
};

const contentFont = 9;
const pageMargins = [28.8, 80, 28.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};

const valueFormatter = (value) => {
  // Check if value is a string and doesn't contain '(' and is greater than or equal to 0
  if (
    typeof value === 'string' &&
    !value.includes('(') &&
    parseFloat(value) >= 0
  ) {
    // Extract the value after the '$' sign and trim any whitespace
    const formattedValue = value.includes('$')
      ? value.split('$')[1].trim()
      : value;
    // Format the extracted value with dollar sign, commas, and two decimal points
    return `$ ${parseFloat(formattedValue).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  // Check if value is a string and contains '(' and ')'
  else if (
    typeof value === 'string' &&
    value.includes(')') &&
    value.includes('(')
  ) {
    // Extract the value between '(' and ')' and trim any whitespace, then remove the first character which is '$'
    const dollarVal = value.includes('$') ? value.split('$')[1].trim() : value;
    const extractedValue = dollarVal.includes('(')
      ? dollarVal.split(')')[0].trim().substring(1)
      : dollarVal;
    // Format the extracted value as negative with dollar sign, commas, and two decimal points
    return `($ ${parseFloat(extractedValue).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  // Check if value is a number and less than 0
  else if (parseFloat(value) < 0) {
    // Format the absolute value as negative with dollar sign, commas, and two decimal points
    return `($ ${Math.abs(parseFloat(value)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  // Handle cases where value is empty, null, or undefined by returning value as it is
  if (value === '' || value === null || typeof value === 'undefined') {
    return value;
  }
  // Handle cases where value is a number or other non-string types by formatting with dollar sign, commas, and two decimal points
  return `$ ${parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getTableRows = (rows, columns) => {
  const dataToReturn = [];
  const headers = columns.map((item) => item.headerName);
  dataToReturn.push([
    {
      text: 'Drug Rebate \n\nRebate Quarter',
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
    },
    {
      text: headers[1],
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
    },
    {
      text: headers[2],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[3],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[4],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[5],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
    {
      text: headers[6],
      alignment: 'center',
      fontSize: contentFont,
      color: 'black',
      bold: true,
    },
  ]);
  const border = [true, true, true, true];
  const noBorder = [false, false, false, false];
  const RedColor='#a81f00';


  for (let i = 0; i < rows.length; i += 1) {
    const row1 = [
      {
        text: rows[i].name,
        fontSize: contentFont,
        color: '',
        border: rows[i].name === '' ? noBorder : border,
      },

      {
        text: valueFormatter(rows[i].Q1),
        fontSize: contentFont,
        alignment: 'right',
        color: rows[i].Q1 < 0 ? RedColor : '',
        border: rows[i].Q1 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].Q2),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].Q2 < 0 ? RedColor : '',
        border: rows[i].Q2 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].Q3),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].Q3 < 0 ? RedColor : '',
        border: rows[i].Q3 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].Q4),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].Q4 < 0 ? RedColor : '',
        border: rows[i].Q4 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].Q5),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].Q5 < 0 ? RedColor : '',
        border: rows[i].Q5 === '' ? noBorder : border,
      },
      {
        text: valueFormatter(rows[i].finalTotal),
        alignment: 'right',
        fontSize: contentFont,
        color: rows[i].finalTotal < 0 ? RedColor : '',
        border: rows[i].finalTotal === '' ? noBorder : border,
      },
    ];
    dataToReturn.push(row1);
    if (rows[i].name === '') {
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
      dataToReturn.push(row1);
    }
  }
  return dataToReturn;
};

const getTableBody = (bodyData, columns, quarter, agencyName) => {
  return [
    {
      style: 'tableExample',
      color: '#444',
      table: {
        widths: [180, 80, 80, 80, 80, 80, 80],
        body: [
          [
            {
              text: 'DRUG REBATE DETAILS',
              style: 'tableHeader',
              colSpan: 3,
              rowSpan: 3,
              alignment: 'left',
              marginTop: 15,
            },
            {},
            {},
            {
              text: { text: `STATE : Maryland`, bold: true },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          [
            {},
            {},
            {},
            {
              text: {
                text: `QUARTER ENDED : ${quarter}`,
                bold: true,
              },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          [
            {},
            {},
            {},
            {
              text: {
                text: `AGENCY : ${agencyName ? agencyName : ''}`,
                bold: true,
              },
              colSpan: 4,
              alignment: 'left',
              fontSize: contentFont,
              color: 'black',
            },
            {},
            {},
            {},
          ],
          ...getTableRows(bodyData, columns),
        ],
      },
    },
  ];
};

const getPdfData = (bodyData, columns, headerData) => {
  const { agencyName } = headerData;
  const { state } = headerData;
  const { quarter } = headerData;
  const content = getTableBody(bodyData, columns, quarter, agencyName);
  const currentYear = new Date().getFullYear();
  return {
    header: (currentPage, pageCount) => {
      return getHeader(currentPage, pageCount, headerData);
    },
    footer: () => {
        const footers = [];
        footers.push({
          text: `${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
          fontSize: 8,
          color: '#636363',
          fontFamily: 'Arial',
          alignment: 'center',
        });

      return footers;
    },
    content,
    pageMargins,
    styles,
    pageSize: {
      width: 780,
      height: 700,
    },
    // pageOrientation: 'landscape',
  };
};

export const exportPDF = (
  data,
  UserDefaultValues,
  columns,
  fileName,
  logInUser,
  dispatch,
  responseCMSRpt
) => {
  const headerData = {
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultValues && UserDefaultValues.userId
        ? UserDefaultValues.userId
        : logInUser,
    reportName: 'CMS 64 9r',
    quarter: responseCMSRpt,
  };

  const pdfData = getPdfData(data, columns, headerData);

  pdfMake.createPdf(pdfData).download(`${fileName}.pdf`);
  dispatch(dispatchTableLoader(false));
};
